import { CAPTCHA_FIELD, PASSWORD_FIELD } from '../constants';
import { IAllContextsProps, IFormikActions } from '../constants/interfaces';
import {
    ACCESS_DENIED_MESSAGE,
    CAPTCHA_REQUIRED_MESSAGE,
    CREDENTIALS_MUST_CHANGE_MESSAGE,
    DEFAULT_ERROR_MESSAGE,
    FORBIDDEN_MESSAGE,
    USER_ACCOUNT_SUSPENDED_MESSAGE,
    USER_EMAIL_INVALID_MESSAGE,
    USER_HAS_NO_PASSWORD_MESSAGE,
    VERIFICATION_REQUIRED_CHECKOUT_EXTRA_MESSAGE,
    VERIFICATION_REQUIRED_CHECKOUT_MESSAGE,
} from '../constants/loginMessages';
import { CREATE_ACCOUNT_MESSAGE } from '../constants/messages';
import { MODAL_TYPES } from '../contexts/FrontendContext';
import {
    trackHandleOnErrorMetric,
    trackPasswordIncorrect,
} from '../tracking/login';
import { trackOpenPasswordlessModal } from '../tracking/passwordless';
import { handleUserIsSoftAccount } from './lookupHandlers';
import { handleUserIsSocialConnected } from './socialHandlers';

/**
 * Catch the error and shows the specific error message to the user.
 */
export const handleOnErrorLogin = ({
    parsedError,
    contexts,
    formikActions,
}: {
    parsedError: any;
    contexts: IAllContextsProps;
    formikActions?: IFormikActions;
}) => {
    const { notificationsContext, configContext, frontendContext } = contexts;
    const { error } = parsedError;
    const analytics = configContext?.analytics;

    switch (error) {
        case 'VERIFICATION_REQUIRED':
            if (configContext?.isCheckoutAuthenticationFlow) {
                trackHandleOnErrorMetric({
                    analytics,
                    errorMessage: error,
                });
                if (
                    configContext?.featureFlags?.launchLoginWithVerificationCode
                ) {
                    frontendContext?.methods.setShowModal(
                        MODAL_TYPES.verification,
                    );
                } else {
                    notificationsContext?.methods.setGeneralNotification({
                        message: VERIFICATION_REQUIRED_CHECKOUT_MESSAGE,
                        extraMessage: VERIFICATION_REQUIRED_CHECKOUT_EXTRA_MESSAGE,
                    });
                }
            } else {
                // The modal opening will handle sending the email
                frontendContext?.methods.setShowModal(MODAL_TYPES.passwordless);

                trackOpenPasswordlessModal({
                    analytics: configContext?.analytics,
                    error: parsedError,
                });
            }

            configContext?.loginMethods?.error?.VERIFICATION_REQUIRED?.action?.();

            break;
        case 'CAPTCHA_REQUIRED':
            //we need to dispatch an action to handle displaying the captcha, so this specific error
            //lives slightly outside of the standard error protocol. Have it here because proxying the
            //specific error for requiring the captcha to this point is cleaner than inserting dispatch
            //directly into the response handling itself
            frontendContext?.methods.setShowCaptcha(true);

            trackHandleOnErrorMetric({
                analytics,
                errorMessage: error,
            });

            // Still not triggering the expected error.
            formikActions?.setFieldError(
                CAPTCHA_FIELD,
                CAPTCHA_REQUIRED_MESSAGE,
            );

            configContext?.loginMethods?.error?.CAPTCHA_REQUIRED?.();
            break;
        case 'USER_PASSWORD_INVALID': // Invalid password, this error is not being returned from backend
        case 'ACCESS_DENIED':
            frontendContext?.methods.setShowForgotPassword(true);
            trackPasswordIncorrect({
                analytics,
            });
            const accessDeniedError =
                configContext?.loginMethods?.error?.ACCESS_DENIED;

            const accessDeniedErrorNotification = accessDeniedError?.getNotification?.();

            notificationsContext?.methods.setGeneralNotification({
                message: ACCESS_DENIED_MESSAGE,
            });
            notificationsContext?.methods.setGeneralNotification({
                // message: ACCESS_DENIED_MESSAGE,
                // extraMessage: accessDeniedErrorNotification?.extraMessage,
                // Delete the message line and uncomment the code avove after checkout implements login with social or passwordles flows
                message:
                    accessDeniedErrorNotification?.extraMessage ??
                    ACCESS_DENIED_MESSAGE,
                cta: {
                    message: accessDeniedErrorNotification?.ctaMessage,
                    action: accessDeniedErrorNotification?.ctaAction,
                },
            });

            formikActions?.setFieldTouched(PASSWORD_FIELD, true);
            formikActions?.setFieldError(PASSWORD_FIELD, ACCESS_DENIED_MESSAGE);

            configContext?.loginMethods?.error?.ACCESS_DENIED?.action?.();

            break;

        case 'USER_EMAIL_INVALID': // Email doesn't exist
            const emailInvalidError =
                configContext?.loginMethods?.error?.USER_EMAIL_INVALID;
            const emailInvalidErrorNotification = emailInvalidError?.getNotification?.();

            notificationsContext?.methods.setGeneralNotification({
                message: USER_EMAIL_INVALID_MESSAGE,
                extraMessage: emailInvalidErrorNotification?.extraMessage,
                cta: {
                    message:
                        emailInvalidErrorNotification?.ctaMessage ??
                        CREATE_ACCOUNT_MESSAGE,
                    action: emailInvalidErrorNotification?.ctaAction,
                },
            });
            emailInvalidError?.action?.();
            break;

        case 'USER_HAS_NO_PASSWORD': // Soft account or account linked with social
            trackHandleOnErrorMetric({
                analytics,
                errorMessage: error,
            });

            handleUserHasNoPassword({
                contexts,
            });

            break;

        case 'FORBIDDEN': // Account Locked
            notificationsContext?.methods.setGeneralNotification({
                message: FORBIDDEN_MESSAGE,
            });

            configContext?.loginMethods?.error?.FORBIDDEN?.();
            break;

        case 'USER_ACCOUNT_SUSPENDED': // Account suspended
            notificationsContext?.methods.setGeneralNotification({
                message: USER_ACCOUNT_SUSPENDED_MESSAGE,
            });

            configContext?.loginMethods?.error?.USER_ACCOUNT_SUSPENDED?.();
            break;

        case 'CREDENTIALS_MUST_CHANGE': // Password must be changed before login
            notificationsContext?.methods.setGeneralNotification({
                message: CREDENTIALS_MUST_CHANGE_MESSAGE,
            });

            frontendContext?.methods.setShowForgotPassword(true);

            configContext?.loginMethods?.error?.CREDENTIALS_MUST_CHANGE?.();
            break;

        default:
            // Unknown error, let the user know and reject the promise
            notificationsContext?.methods.setGeneralNotification({
                message: DEFAULT_ERROR_MESSAGE,
            });

            configContext?.loginMethods?.error?.DEFAULT_ERROR?.();
            break;
    }
    return Promise.resolve();
};

/**
 * This scenario is complex to reproduce. The lookup call should prevent a this
 * because the no-password would have appeared in that call preventing a login
 * call to be made.
 */
export const handleUserHasNoPassword = ({
    contexts,
}: {
    contexts: IAllContextsProps;
}) => {
    const { notificationsContext, configContext, userContext } = contexts;
    const socialConnected = userContext?.state.lookup.socialConnected;

    configContext?.loginMethods?.error?.USER_HAS_NO_PASSWORD?.action?.();

    notificationsContext?.methods.setGeneralNotification({
        message: USER_HAS_NO_PASSWORD_MESSAGE,
    });

    if (socialConnected?.length) {
        handleUserIsSocialConnected({
            contexts,
        });
    } else {
        /** This is a weird scenario. The user would have to have a succesful
         * lookup with an existent account but then login would need to say
         * that there is no password among the sign_up_methods in the response
         */
        handleUserIsSoftAccount({ contexts });
    }
};
